import { cropStyle, stepView, type Step } from '@nolens/domain';
import { useState } from 'react';
import { AnnotationLayer } from './Annotations';
import { cx } from './cx';

/**
 * Captura de um passo com zoom no sítio do clique e o marcador. A imagem é sempre a
 * inteira: o recorte é feito aqui (CSS), por isso desligar o zoom não perde nada.
 * Igual na extensão, na app web e na página pública; só muda de onde vem `src`.
 */
export function Shot({
  step,
  src,
  zoom = step.zoom !== false,
  className,
  pii = false,
}: {
  step: Step;
  /** Endereço da imagem; null enquanto carrega. */
  src: string | null | undefined;
  zoom?: boolean;
  className?: string;
  /** Mostra os contornos dos dados pessoais por rever. */
  pii?: boolean;
}) {
  // Proporção da imagem real (o tamanho do ecrã indicado pode não incluir a barra de scroll)
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  if (!step.imageId) return null;
  const vw = natural?.w || step.viewport?.w || 16;
  const vh = natural?.h || step.viewport?.h || 10;
  const { crop, marker, showMarker } = stepView({ ...step, zoom });
  const aspect = (vw * crop.w) / (vh * crop.h);
  const m = showMarker ? marker : null;
  return (
    <div
      className={cx('relative w-full overflow-hidden rounded-[10px] bg-surface-2', className)}
      style={{ aspectRatio: String(aspect) }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          draggable={false}
          className="absolute max-w-none select-none"
          style={{ ...cropStyle(crop), height: 'auto' }}
          onLoad={(e) =>
            setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
          }
        />
      ) : (
        <div className="absolute inset-0 animate-pulse bg-surface-2" />
      )}
      <AnnotationLayer annotations={step.annotations} crop={crop} aspect={aspect} />
      {pii &&
        step.pii?.map((a, i) => (
          <div
            key={i}
            className="pointer-events-none absolute rounded-[3px] border-2 border-dashed border-attention bg-attention-soft/40"
            style={{
              left: ((a.x - crop.x) / crop.w) * 100 + '%',
              top: ((a.y - crop.y) / crop.h) * 100 + '%',
              width: (a.w / crop.w) * 100 + '%',
              height: (a.h / crop.h) * 100 + '%',
            }}
          />
        ))}
      {m && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute size-9 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-accent bg-accent/25 shadow-[0_0_0_4px_rgba(255,255,255,.6)]"
          style={{ left: m.x * 100 + '%', top: m.y * 100 + '%' }}
        />
      )}
    </div>
  );
}

/** Estilo de um bloco de texto (dica, aviso, nota, secção). */
export const TONE_STYLE = {
  tip: 'bg-positive-soft shadow-[inset_4px_0_0_var(--positive)]',
  alert: 'bg-attention-soft shadow-[inset_4px_0_0_var(--attention)]',
  info: 'bg-surface-2 shadow-[inset_4px_0_0_var(--border-strong)]',
  heading: 'bg-transparent',
} as const;
